import dayjs from 'dayjs';
import * as yup from 'yup';

import { isWholeNumber, MONEY_VALIDATION_MESSAGES } from '@/shared/validators';
import {
  validateMaxLeaseDuration,
  validateMinLeaseDuration,
} from '@/shared/utils/leaseDurationMonths.util';

import { INVITE_LEASE_DETAILS_VALIDATION_MESSAGES as MSG, ScreeningResult } from './constants';

// Config-derived validation for the shared engine. Every field stores the
// engine's convention (money/date as strings), so these are string/object-based
// Yup schemas that the engine concatenates onto each field's base. They port the
// exact rules + messages of the legacy `inviteLeaseDetailsSchema`; the only
// change is operating on string storage instead of number/Date.

const MIN_LEASE_DURATION_MONTHS = 3;
const MAX_LEASE_DURATION_MONTHS = 36;
const MONEY_MAX = 50000;

const isBlank = (value: unknown): boolean => value == null || value === '';
// Callers always gate with `isBlank` first, so this is only reached for
// non-blank values. Non-string inputs collapse to `NaN` instead of being
// stringified via `Object.prototype.toString` — every consumer (`Number.isFinite`,
// numeric `<=` / `>=`) treats NaN as a failed check, which is the right behavior.
const parseMoney = (value: unknown): number => {
  if (typeof value !== 'string') {
    return Number.NaN;
  }
  return Number(value.replaceAll(',', ''));
};

// Date bounds are relative to "now", recomputed per schema build (matching the
// legacy schema, which evaluated them at creation time).
const oneYearPast = (): Date =>
  dayjs(new Date().setHours(0, 0, 0, 0))
    .subtract(1, 'year')
    .add(1, 'day')
    .toDate();
const oneYearFuture = (): Date =>
  dayjs(new Date().setHours(23, 59, 59, 999))
    .add(1, 'year')
    .subtract(1, 'day')
    .toDate();
const elevenYearsFuture = (): Date =>
  dayjs(new Date().setHours(23, 59, 59, 999))
    .add(11, 'year')
    .subtract(1, 'day')
    .toDate();

// ---- Lease information ----

export const addressValidate = yup
  .object()
  .shape({
    monolith_building_id: yup.number().required(MSG.ADDRESS_REQUIRED_MESSAGE),
    property_alpha_id: yup.string().required(MSG.ADDRESS_NOT_FOUND),
    address_alpha_id: yup.string().required(MSG.ADDRESS_NOT_FOUND),
    source: yup.string().optional().nullable(),
  })
  .typeError(MSG.ADDRESS_NOT_FOUND)
  .required(MSG.ADDRESS_REQUIRED_MESSAGE);

export const apartmentNumberValidate = yup
  .string()
  .nullable()
  .max(100, MSG.APARTMENT_NUMBER_TOO_LONG);

export const moneyValidate = yup
  .string()
  .nullable()
  .test('numeric', MSG.MONEY_FIELD_TYPE_ERROR, (v) => isBlank(v) || Number.isFinite(parseMoney(v)))
  .test('min', MSG.MONEY_FIELD_MIN, (v) => isBlank(v) || parseMoney(v) >= 0)
  .test('max', MSG.MONEY_FIELD_MAX, (v) => isBlank(v) || parseMoney(v) <= MONEY_MAX);

export const grossMonthlyRentValidate = moneyValidate.test(
  'whole-number',
  MONEY_VALIDATION_MESSAGES.MUST_BE_WHOLE_NUMBER,
  (v) => isBlank(v) || isWholeNumber(v),
);

export const leaseStartValidate = (): yup.AnySchema =>
  yup
    .string()
    .nullable()
    .test(
      'min-one-year-past',
      MSG.DATE_ONE_YEAR_PAST,
      (v) => isBlank(v) || dayjs(v).toDate() >= oneYearPast(),
    )
    .test(
      'max-one-year-future',
      MSG.DATE_ONE_YEAR_FUTURE,
      (v) => isBlank(v) || dayjs(v).toDate() <= oneYearFuture(),
    );

export const leaseEndValidate = (useNewCoverages?: boolean): yup.AnySchema => {
  let schema = yup
    .string()
    .nullable()
    .test('after-start', MSG.LEASE_END_AFTER_START, function (v) {
      if (isBlank(v)) {
        return true;
      }
      return dayjs(this.parent.leaseStartDate as string) < dayjs(v);
    })
    .test(
      'min-one-year-past',
      MSG.DATE_ONE_YEAR_PAST,
      (v) => isBlank(v) || dayjs(v).toDate() >= oneYearPast(),
    )
    .test(
      'max-eleven-years-future',
      MSG.DATE_ONE_YEAR_FUTURE,
      (v) => isBlank(v) || dayjs(v).toDate() <= elevenYearsFuture(),
    )
    .test('min-three-months', MSG.LEASE_DURATION_MIN_THREE_MONTHS, function (v) {
      if (isBlank(v) || !this.parent.leaseStartDate) {
        return true;
      }
      return validateMinLeaseDuration(
        new Date(this.parent.leaseStartDate as string),
        new Date(v as string),
        MIN_LEASE_DURATION_MONTHS,
      );
    });

  if (useNewCoverages) {
    schema = schema.test(
      'max-thirty-six-months',
      MSG.LEASE_DURATION_MAX_THIRTY_SIX_MONTHS,
      function (v) {
        if (isBlank(v) || !this.parent.leaseStartDate) {
          return true;
        }
        return validateMaxLeaseDuration(
          new Date(this.parent.leaseStartDate as string),
          new Date(v as string),
          MAX_LEASE_DURATION_MONTHS,
        );
      },
    );
  }

  return schema;
};

// ---- Applicants ----

export const applicantEmailValidate = yup.string().email(MSG.APPLICANT_EMAIL_INVALID);

// Array-level case-insensitive uniqueness (the per-entry `email`/`required`
// rules live on the entry field's own config).
export const applicantsUniqueValidate = yup
  .array()
  .test('unique', MSG.APPLICANT_EMAIL_UNIQUE, function (values, { createError }) {
    const entries = values as Array<{ email?: string }> | undefined;
    if (!entries?.length) {
      return true;
    }
    const emails = entries.map(({ email }) => (email ?? '').toLowerCase());
    const hasDuplicate = new Set(emails).size !== emails.length;
    return hasDuplicate
      ? createError({ path: 'applicants', message: MSG.APPLICANT_EMAIL_UNIQUE_IGNORING_CASE })
      : true;
  });

// ---- Screening + coverage ----

export const approvalStatusValidate = (useNewCoverages?: boolean): yup.AnySchema => {
  const allowed = useNewCoverages
    ? ['', ScreeningResult.Approved, ScreeningResult.ApprovedWithConditions, ScreeningResult.Denied]
    : [ScreeningResult.Approved, ScreeningResult.ApprovedWithConditions, ScreeningResult.Denied];
  return yup.string().nullable().oneOf(allowed, MSG.INVALID_SCREENING_RESULT);
};

export const monthsOfCoverageValidate = yup
  .string()
  .nullable()
  .test(
    'numeric',
    MSG.MONTHS_OF_COVERAGE_REQUIRED,
    (v) => isBlank(v) || Number.isFinite(parseMoney(v)),
  )
  .test('min', MSG.MONTHS_OF_COVERAGE_MIN, (v) => isBlank(v) || parseMoney(v) >= 0)
  .test('max', MSG.MONTHS_OF_COVERAGE_MAX, (v) => isBlank(v) || parseMoney(v) <= 100);

export const securityDepositReplacementAmountValidate = (
  selfEnrolledSDRLimit?: number,
): yup.AnySchema => {
  if (!selfEnrolledSDRLimit) {
    return moneyValidate;
  }
  return moneyValidate.test(
    'sdr-cap',
    MSG.amountCannotExceedRentMonths(selfEnrolledSDRLimit),
    function (v) {
      if (isBlank(v)) {
        return true;
      }
      const rent = parseMoney(this.parent.grossMonthlyRent) || 0;
      return parseMoney(v) <= rent * selfEnrolledSDRLimit;
    },
  );
};
